// @flow

import React, { useEffect, createRef, useMemo, useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';
import classnames from 'classnames';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { debounce as _debounce } from 'lodash';
import {
  AnalyticsContext,
  AnalyticsLocationConsumer,
  trackEvent
} from 'latitude-analytics';
import SvgInline from '@latitude/svg-inline';
import {
  ALIGN,
  BREAKPOINT,
  COLOR,
  MARGIN,
  REBRAND_COLOR
} from '@latitude/core/utils/constants';
import { pxAsNumber } from '@latitude/core/helpers';
import { List, ListItem } from '@latitude/list';
import {
  Card,
  CardText,
  CardIcon,
  CardBody,
  FeatureSliderOuterWrapper,
  FeatureSliderInnerWrapper,
  Slider
} from './FeaturesSlider.styled';
import { Link } from '@latitude/link';
import Typography from '@latitude/typography';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const responsiveConfig = [
  {
    breakpoint: pxAsNumber(BREAKPOINT.LG),
    settings: { slidesToShow: 3 }
  },
  {
    breakpoint: pxAsNumber(BREAKPOINT.MD),
    settings: { slidesToShow: 2 }
  },
  {
    breakpoint: pxAsNumber(BREAKPOINT.SM),
    settings: 'unslick' // hides slick when breakpoint is smaller than SM
  }
];

const FeaturesSlider = ({
  id,
  className,
  data = [],
  arrows = false,
  dots = true,
  infinite = false,
  speed = 300,
  initialSlide = 0,
  slidesToScroll = 1,
  slidesToShow = 4,
  responsive = responsiveConfig,
  backgroundColor,
  contentAlign = ALIGN.CENTER,
  whiteTiles = false,
  heading,
  subheading,
  enableParallelHeader
}) => {
  const [analytics] = useContext(AnalyticsContext);

  const cardsRef = useMemo(() => {
    const refs = {};
    data.forEach((card, index) => {
      refs[card.id || index] = createRef();
    });
    return refs;
  }, [data]);

  const setTitleHeight = height => {
    Object.keys(cardsRef).forEach(key => {
      const ref = cardsRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeHeadingHeight = () => {
    let max = 0;
    Object.keys(cardsRef).forEach(key => {
      const element = cardsRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setTitleHeight(max);
  };

  const handleWindowResize = () => {
    setTitleHeight(); // resets height(s) to 'auto'
    if (window.innerWidth > BREAKPOINT.SM) {
      equalizeHeadingHeight();
    }
  };

  const debouncedHandleResize = _debounce(handleWindowResize, 100);

  const isMobile =
    typeof window !== 'undefined'
      ? window.innerWidth <= parseInt(BREAKPOINT.SM)
      : false;

  useEffect(() => {
    if (window.innerWidth > BREAKPOINT.SM) {
      equalizeHeadingHeight();
    }
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [data, cardsRef]);

  const calculatedSlidesToShow =
    slidesToShow > data.length ? data.length : slidesToShow;

  const calculatedResponsive = responsive.map(config => ({
    ...config,

    settings:
      config.settings === 'unslick'
        ? {
            slidesToShow: data.length
          }
        : {
            ...config.settings,
            slidesToShow:
              config.settings.slidesToShow > data.length
                ? data.length
                : config.settings.slidesToShow
          }
  }));

  // Convert all anchor elements to Link components with analytics
  const createTransform = location => node => {
    if (node?.name === 'a' && node?.attribs?.href) {
      return (
        <Link
          href={node.attribs.href}
          trackEventData={{
            category: 'text-link',
            action: 'internal-link',
            location,
            label: node?.children[0]?.data
          }}
        >
          {node?.children[0]?.data}
        </Link>
      );
    }
  };

  const getCardRichTextOptions = location =>
    getCdrEntryBindingRichTextOptions({
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
        [INLINES.HYPERLINK]: (node, children) => (
          <Link
            href={node?.data?.uri}
            trackEventData={{
              category: 'text-link',
              action: 'internal-link',
              location,
              label: children
            }}
          >
            {children}
          </Link>
        )
      }
    });

  const renderCardText = card =>
    card.richText
      ? documentToReactComponents(
          card.richText,
          getCardRichTextOptions(card.title)
        )
      : ReactHtmlParser(card.text, {
          transform: createTransform(card.title)
        });

  return (
    <FeatureSliderOuterWrapper
      id={id}
      className={classnames('lfs-features-slider', className)}
      backgroundColor={backgroundColor}
    >
      <FeatureSliderInnerWrapper>
        {heading && (
          <Typography.SH1 semibold color={REBRAND_COLOR.BLACK}>
            {ReactHtmlParser(heading)}
          </Typography.SH1>
        )}

        <AnalyticsLocationConsumer>
          {location => (
            <Slider
              arrows={arrows}
              dots={dots}
              infinite={infinite}
              initialSlide={initialSlide}
              slidesToScroll={slidesToScroll}
              slidesToShow={calculatedSlidesToShow}
              speed={speed}
              responsive={calculatedResponsive}
              whiteTiles={whiteTiles}
              afterChange={slideNumber => {
                // Get viewport
                const viewportWidth = Math.max(
                  document.documentElement.clientWidth,
                  window.innerWidth || 0
                );

                // Find which responsive breakpoint to determine number of slides in view
                // Then create label concatenating title from each slide
                let label = '';
                for (let i = responsiveConfig.length - 1; i >= 0; i--) {
                  const config = responsiveConfig[i];
                  if (viewportWidth <= config.breakpoint) {
                    const slidesInViewport = data.slice(
                      slideNumber,
                      slideNumber + config.settings.slidesToShow
                    );

                    label = slidesInViewport
                      .map(slide => slide.title)
                      .join('::');
                    break;
                  }
                }
                trackEvent(analytics, {
                  category: 'slider',
                  action: 'interaction',
                  label,
                  location: heading
                });
              }}
            >
              {data.map((card, i) => (
                <Card
                  key={i}
                  className={classnames('lfs-card', {
                    'white-tiles': whiteTiles
                  })}
                  align={contentAlign}
                >
                  {isMobile && enableParallelHeader ? (
                    <CardText
                      className="lfs-card-text"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '8px'
                        }}
                      >
                        {card.icon && (
                          <SvgInline className="h-100 mr-2" name={card.icon} />
                        )}
                        <div
                          ref={cardsRef[card.id || i]}
                          style={{ flexGrow: 1, textAlign: 'center' }}
                        >
                          <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
                            {card.title}
                          </Typography.SH4>
                        </div>
                      </div>

                      <CardBody className="lfs-card-body">
                        {(card.richText || card.text) && (
                          <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                            {renderCardText(card)}
                          </Typography.Body>
                        )}
                        {card.list && (
                          <List
                            dotColor={COLOR.BLUE_LIGHT}
                            marginLeft={MARGIN.M32}
                          >
                            {card.list.map(item => (
                              <ListItem>
                                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                                  {ReactHtmlParser(item)}
                                </Typography.Body>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        {card.jsx}
                      </CardBody>
                    </CardText>
                  ) : (
                    <>
                      {card.icon && (
                        <CardIcon className="lfs-card-icon">
                          <SvgInline className="h-100 w-100" name={card.icon} />
                        </CardIcon>
                      )}
                      <CardText className="lfs-card-text">
                        <div
                          ref={cardsRef[card.id || i]}
                          style={{ flexGrow: 1, textAlign: 'center' }}
                        >
                          <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
                            {card.title}
                          </Typography.SH4>
                        </div>
                        <CardBody className="lfs-card-body">
                          {(card.richText || card.text) && (
                            <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                              {renderCardText(card)}
                            </Typography.Body>
                          )}
                          {card.list && (
                            <List
                              dotColor={COLOR.BLUE_LIGHT}
                              marginLeft={MARGIN.M32}
                            >
                              {card.list.map(item => (
                                <ListItem>
                                  {' '}
                                  <Typography.Body
                                    color={REBRAND_COLOR.BLACK_80}
                                  >
                                    {ReactHtmlParser(item)}
                                  </Typography.Body>
                                </ListItem>
                              ))}
                            </List>
                          )}
                          {card.jsx}
                        </CardBody>
                      </CardText>
                    </>
                  )}
                </Card>
              ))}
            </Slider>
          )}
        </AnalyticsLocationConsumer>
      </FeatureSliderInnerWrapper>
    </FeatureSliderOuterWrapper>
  );
};

export default FeaturesSlider;
